import type {
  HardwareDelivery,
  InstallationFormValues,
  InstallationMethod,
  PickupAddress,
  TechnicianOptionResponse,
} from '@t/checkout.types';
import type { AccessRestriction } from '@t/serviceQualification.types';

export const NOVUS_PICKUP_ADDRESS = '300 - 112 3rd Ave E Vancouver BC V5T 1C8';

export const DROPOFF_NOTE_TEXT =
  'Note: Your Equipment will be delivered to your service address on the activation date. ' +
  'A coordinator will contact you within 24 business hours to confirm the drop-off window';

export const textFieldSx = {
  '& .MuiOutlinedInput-input::placeholder': { fontSize: '16px', fontWeight: 400, lineHeight: '100%' },
  '& .MuiOutlinedInput-root': { borderRadius: '6px' },
};

export const formatSlotTime = (iso: string): string => {
  const d = new Date(iso);
  const datePart = d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const timePart = d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${datePart} \u2022 ${timePart}`;
};

export const formatSlotTimeRange = (startIso: string, endIso: string): string => {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const datePart = start.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const startTime = start.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  const endTime = end.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${datePart} \u2022 ${startTime} - ${endTime}`;
};

export const formatSlotDate = (iso: string): string =>
  new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export const formatPickupAddress = (addr: PickupAddress): string =>
  [addr.street, addr.city, addr.state, addr.postalCode, addr.country].filter(Boolean).join(', ');

export const toDateOnly = (d: Date): string => {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const getMinDate = (minLeadTimeDays: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + minLeadTimeDays);
  return toDateOnly(d);
};

export const getDropoffRestrictedDates = (options: TechnicianOptionResponse[]): string[] => {
  const dropoff = options.find(
    (o) => o.installationMethod === 'SELF' && o.deliveryMethod === 'DROPOFF',
  );
  return Object.keys(dropoff?.Day_Restricted ?? {}).map((k) => k.replaceAll('.', '-'));
};

export const getLeadTimeDays = (
  options: TechnicianOptionResponse[],
  method: InstallationMethod,
  delivery: HardwareDelivery | undefined
): number => {
  if (method === 'TECHNICIAN') {
    return options.find((o) => o.installationMethod === 'TECHNICIAN')?.leadTimeDays ?? 0;
  }
  return options.find((o) => o.installationMethod === 'SELF' && o.deliveryMethod === delivery)?.leadTimeDays ?? 0;
};

export const getInstallationDateLabel = (isSelf: boolean, slot: { startTime: string; endTime?: string }): string => {
  if (isSelf) return formatSlotDate(slot.startTime);
  if (slot.endTime) return formatSlotTimeRange(slot.startTime, slot.endTime);
  return formatSlotTime(slot.startTime);
};

export const buildRestrictionDescription = (restrictions?: AccessRestriction[]): string => {
  const instructions = restrictions
    ?.filter((r) => r.status === 'ACTIVE' && (r.impact === 'CUSTOMER' || r.impact === 'ALL'))
    .map((r) => r.instructions)
    .filter((text): text is string => !!text);

  if (!instructions?.length) return '';

  return instructions.join(' ');
};

const resolveDefaultMethod = (technicianOptions: TechnicianOptionResponse[]): InstallationMethod =>
  technicianOptions.find((o) => o.defaultMethod)?.installationMethod ??
  technicianOptions[0]?.installationMethod ??
  'TECHNICIAN';

const resolveInstallationMethod = (
  saved: InstallationMethod | undefined,
  technicianOptions: TechnicianOptionResponse[]
): InstallationMethod => {
  const available = technicianOptions.map((o) => o.installationMethod);
  if (saved && available.includes(saved)) return saved;
  return resolveDefaultMethod(technicianOptions);
};

export const buildInitialValues = (
  savedValues: InstallationFormValues | null,
  technicianOptions: TechnicianOptionResponse[]
): InstallationFormValues => {
  const installationMethod = resolveInstallationMethod(savedValues?.installationMethod, technicianOptions);
  const isSelf = installationMethod === 'SELF';
  return {
    installationMethod,
    hardwareDelivery: isSelf ? savedValues?.hardwareDelivery ?? 'PICKUP' : undefined,
    deliveryInstructions: savedValues?.deliveryInstructions ?? '',
    specialInstructions: savedValues?.specialInstructions ?? '',
    selectedSlotId: savedValues?.selectedSlotId ?? null,
    selectedDate: savedValues?.selectedDate ?? null,
    selectedTimeSlot: savedValues?.selectedTimeSlot ?? null,
  };
};
